export type HelpResourceKey = "documentation" | "community";

export interface HelpResourceLink {
  href: string;
  external: boolean;
  displayUrl: string;
}

const SITE_ORIGIN = "https://tellerwand.com";

function trimEnv(value: string | undefined): string {
  return (value ?? "").trim();
}

function readDocsPortalUrl(): string {
  return trimEnv(import.meta.env.VITE_DOCS_PORTAL_URL);
}

function readCommunityPortalUrl(): string {
  return trimEnv(import.meta.env.VITE_COMMUNITY_PORTAL_URL);
}

/**
 * Build Help Resource.
 */
function buildHelpResource(externalUrl: string, interimPath: string): HelpResourceLink {
  if (externalUrl) {
    return {
      href: externalUrl,
      external: true,
      displayUrl: externalUrl,
    };
  }

  return {
    href: interimPath,
    external: false,
    displayUrl: `${SITE_ORIGIN}${interimPath}`,
  };
}

/** Resolve documentation portal URL (external when configured, otherwise in-app FAQ). */
export function getDocumentationLink(): HelpResourceLink {
  return buildHelpResource(readDocsPortalUrl(), "/faq");
}

/** Resolve community portal URL (external when configured, otherwise in-app blog). */
export function getCommunityLink(): HelpResourceLink {
  return buildHelpResource(readCommunityPortalUrl(), "/blog");
}

export function getHelpResourceLink(key: HelpResourceKey): HelpResourceLink {
  if (key === "documentation") {
    return getDocumentationLink();
  }
  return getCommunityLink();
}

/** Navigate in-app or open external help resource in a new tab. */
export function openHelpResource(key: HelpResourceKey, navigate: (path: string) => void): void {
  const resource = getHelpResourceLink(key);
  if (resource.external) {
    window.open(resource.href, "_blank", "noopener,noreferrer");
    return;
  }
  navigate(resource.href);
}
